import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { downloadBlob } from "@/lib/workflow";
import { useAuth } from "@/context/AuthContext";
import { StageBadge } from "@/components/PartnerServices";
import { KindBadge, TierBadge, TIER_META } from "@/pages/CustomerList";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BannerGallery } from "@/components/banner/BannerGallery";
import { CalendarRange, FileDown, Images, ImageOff, Film, Play, List, LayoutGrid, Share2, Copy, ExternalLink, RefreshCw } from "lucide-react";

const SOON_DAYS = 90;
const fmt = (v, unit) => Number(v).toFixed(unit === "px" ? 0 : 2).replace(".", ",");
const size = (s) => (s.width != null && s.height != null ? `${fmt(s.width, s.unit)} × ${fmt(s.height, s.unit)} ${s.unit || "m"}` : "");
const deDate = (d) => (d ? d.split("-").reverse().join(".") : "");
const daysUntil = (d, today) => Math.round((new Date(d) - new Date(today)) / 86400000);

function status(space, today) {
  const now = space.bookings.filter((b) => !b.from || b.from <= today);
  if (!now.length) return "frei";
  return now.some((b) => b.until && daysUntil(b.until, today) <= SOON_DAYS) ? "bald" : "belegt";
}
const STYLE = {
  frei: ["bg-emerald-50", "bg-emerald-100 text-emerald-700", "frei"],
  belegt: ["", "bg-slate-100 text-slate-700", "belegt"],
  bald: ["bg-amber-50", "bg-amber-100 text-amber-800", "läuft bald aus"],
};

function Booking({ b, today, sid, plain }) {
  const d = b.until ? daysUntil(b.until, today) : null;
  return (
    <div className="flex flex-wrap items-baseline gap-x-2">
      {plain ? <span className="font-medium text-slate-900">{b.name}</span> : <Link to={`/partner/${b.id}`} className="font-medium text-slate-900 hover:underline" data-testid={`plan-partner-${b.id}`}>{b.name}</Link>}
      <KindBadge kind={b.kind} id={`${b.id}-${sid}`} /><TierBadge tier={b.tier} id={`${b.id}-${sid}`} />
      <span className={`text-xs ${d != null && d <= SOON_DAYS ? "text-amber-700 font-semibold" : "text-slate-500"}`}>
        {b.from && b.from > today ? <span className="text-sky-700 font-medium" data-testid={`plan-planned-${b.id}-${sid}`}>geplant ab {deDate(b.from)} · </span> : null}{b.until ? `bis ${deDate(b.until)}${d <= SOON_DAYS ? ` (noch ${d} Tage)` : ""}` : "unbefristet"}{b.until && !b.own_term ? " · Partnerschaft" : ""}
      </span>
    </div>
  );
}

const visibleSpaces = (type, today, filter, tier) => type.spaces.map((s) => (tier ? { ...s, bookings: s.bookings.filter((b) => b.tier === tier) } : s))
  .filter((s) => (!tier || s.bookings.length) && (filter === "alle" || status(s, today) === filter));

const authPreview = (g) => `/partner-graphics/item/${g.id}/preview`;
const authVideo = (g) => `/partner-graphics/item/${g.id}/video`;

function Thumb({ g, bg, previewPath = authPreview, videoPath = authVideo, title }) {
  const [src, setSrc] = useState(null);
  const [big, setBig] = useState(false);
  useEffect(() => {
    let url;
    if (g?.preview_id) api.get(previewPath(g), { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [g, previewPath]);
  return (
    <>
      <button type="button" disabled={!src && !g?.is_video} onClick={() => setBig(true)} className={`relative w-full h-36 flex items-center justify-center p-4 border-b border-slate-200 ${src || g?.is_video ? "cursor-zoom-in hover:opacity-90" : "cursor-default"}`}
        style={{ backgroundColor: src ? bg || "#ffffff" : "#f1f5f9" }} data-testid="gallery-thumb">
        {src ? <img src={src} alt={g.filename} className="max-h-full max-w-full object-contain" /> : <div className="text-center text-xs text-slate-400">{g?.is_video ? <Film size={22} className="mx-auto mb-1" /> : <ImageOff size={22} className="mx-auto mb-1" />}{g === undefined ? "frei – keine Grafik" : g?.is_video ? "Video" : "keine Grafik"}</div>}
        {g?.is_video && <span className="absolute inset-0 grid place-items-center"><span className="h-10 w-10 rounded-full bg-black/60 text-white grid place-items-center"><Play size={18} fill="white" /></span></span>}
      </button>
      <Dialog open={big} onOpenChange={setBig}>
        <DialogContent className="max-w-4xl" data-testid="gallery-lightbox">
          <DialogHeader><DialogTitle className="text-base">{title}</DialogTitle></DialogHeader>
          <div className="rounded-lg border border-slate-200 flex items-center justify-center p-8 min-h-[280px]" style={{ backgroundColor: bg || "#ffffff" }}>
            {g?.is_video
              ? <video controls autoPlay poster={src || undefined} src={`${api.defaults.baseURL}${videoPath(g)}`} className="max-h-[65vh] max-w-full" data-testid="gallery-video" />
              : src && <img src={src} alt="Grafik groß" className="max-h-[65vh] max-w-full object-contain" />}
          </div>
          <p className="text-xs text-slate-400">{g?.filename}</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function TypeGallery({ type, today, filter = "alle", tier = "", gfx, previewPath, videoPath, plain }) {
  const cards = visibleSpaces(type, today, filter, tier).filter((s) => !s.banner).flatMap((s) => (s.bookings.length ? s.bookings.map((b) => ({ s, b })) : [{ s, b: null }]));
  if (!cards.length) return null;
  return (
    <section data-testid={`plan-gallery-${type.id}`}>
      <h2 className="font-semibold text-slate-900 mb-3">{type.name} <span className="text-slate-400 font-normal text-sm">({cards.length})</span></h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {cards.map(({ s, b }) => (
          <div key={`${s.id}-${b?.id || "frei"}`} className={`bg-white border rounded-xl overflow-hidden shadow-sm ${b ? "border-slate-200" : "border-emerald-200"}`} data-testid={`plan-card-${s.id}`}>
            <Thumb g={b ? (gfx ? gfx[`${b.id}|${s.id}`] : b.graphic) || null : undefined} bg={type.preview_bg} previewPath={previewPath} videoPath={videoPath}
              title={`${[s.name, s.location].filter(Boolean).join(" · ")}${b ? ` – ${b.name}` : ""}`} />
            <div className="p-3 text-sm space-y-1">
              <div className="flex items-baseline justify-between gap-2"><span><b>{s.name}</b>{s.location && <span className="text-slate-500"> · {s.location}</span>}</span><span className="text-xs text-slate-500 whitespace-nowrap">{size(s)}</span></div>
              {b ? <>
                <Booking b={b} today={today} sid={s.id} plain={plain} />
                <div className="flex gap-1.5 text-[10px] font-bold uppercase">
                  <StageBadge st={b.state} testid={`plan-stage-${s.id}`} />
                  <span className={`px-1.5 py-0.5 rounded ${b.state?.material ? "text-emerald-700" : "text-amber-700"}`}>Material {b.state?.material ? "vorhanden" : "fehlt"}</span>
                </div>
              </> : <div className="text-emerald-700 font-medium">frei</div>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TypeTable({ type, today, filter, tier }) {
  const rows = visibleSpaces(type, today, filter, tier);
  if (!rows.length) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`plan-type-${type.id}`}>
      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-1">
        <h2 className="font-semibold text-slate-900">{type.name}</h2>
        <span className="text-xs text-slate-500">{type.exclusive ? "je Fläche ein Partner" : "mehrere Partner je Fläche"}</span>
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm min-w-[640px]">
        <thead className="text-xs text-slate-500 text-left"><tr><th className="px-5 py-2 font-medium">Fläche</th><th className="px-3 py-2 font-medium">Maße</th><th className="px-3 py-2 font-medium">Partner & Laufzeit</th><th className="px-5 py-2 font-medium text-right">Status</th></tr></thead>
        <tbody>
          {rows.map((s) => { const st = status(s, today); const [row, badge, label] = STYLE[st]; return (
            <tr key={s.id} className={`border-t border-slate-100 ${row}`} data-testid={`plan-space-${s.id}`}>
              <td className="px-5 py-2.5"><span className="font-medium text-slate-900">{s.name}</span>{s.location && <span className="text-slate-500"> · {s.location}</span>}</td>
              <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{size(s)}</td>
              <td className="px-3 py-2.5 space-y-0.5">{s.bookings.length ? s.bookings.map((b) => <Booking key={b.id} b={b} today={today} sid={s.id} />) : <span className="text-emerald-700">– frei –</span>}</td>
              <td className="px-5 py-2.5 text-right"><span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${badge}`} data-testid={`plan-status-${s.id}`}>{label}</span></td>
            </tr>
          ); })}
        </tbody>
      </table></div>
    </section>
  );
}

function GalleryShare({ types }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  useEffect(() => { if (open) api.get("/gallery-share").then((r) => setD(r.data)).catch(() => {}); }, [open]);
  const save = async (body) => {
    const { data } = await api.post("/gallery-share", body);
    setD(data);
    toast.success(body.renew ? "Neuer Link erstellt – der alte gilt nicht mehr" : body.type_ids ? "Freigabe aktualisiert" : data.enabled ? "Galerie-Link aktiviert" : "Galerie-Link deaktiviert");
  };
  const toggleType = (tid) => {
    const cur = d.type_ids || [];
    save({ enabled: d.enabled, type_ids: cur.includes(tid) ? cur.filter((x) => x !== tid) : [...cur, tid] });
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="gallery-share-button"><Share2 size={15} className="mr-1.5" /> Galerie teilen</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="gallery-share-dialog">
          <DialogHeader><DialogTitle>Galerie per Link teilen</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Mit dem Link können z. B. Vorstand oder Platzpflege alle Werbeflächen und Werbebanner mit Grafik, Partner und Laufzeit ohne Anmeldung ansehen – nur lesen, ohne Kontaktdaten.</p>
          {d && <>
            <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={d.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="gallery-share-toggle" /> Link {d.enabled ? "aktiv" : "deaktiviert"}</label>
            {d.enabled && d.url && <>
              <div className="text-sm">
                <div className="font-medium text-slate-700 mb-1">Freigegebene Leistungsarten</div>
                <div className="flex flex-wrap gap-1.5">
                  {types.map((t) => { const on = (d.type_ids || []).includes(t.id); return (
                    <button key={t.id} type="button" onClick={() => toggleType(t.id)} data-testid={`gallery-share-type-${t.id}`}
                      className={`px-2.5 py-1 rounded-full border text-xs ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{t.name}</button>
                  ); })}
                </div>
                <p className="text-xs text-slate-400 mt-1">{(d.type_ids || []).length ? "Nur die markierten Leistungsarten sind über den Link sichtbar." : "Keine Auswahl = alle Leistungsarten sichtbar."}</p>
              </div>
              <div className="text-sm font-mono bg-slate-50 border border-slate-200 rounded-md px-3 py-2 break-all" data-testid="gallery-share-url">{d.url}</div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(d.url).then(() => toast.success("Link kopiert"))} data-testid="gallery-share-copy"><Copy size={14} className="mr-1.5" /> Kopieren</Button>
                <Button size="sm" variant="outline" onClick={() => window.open(d.url, "_blank")} data-testid="gallery-share-open"><ExternalLink size={14} className="mr-1.5" /> Öffnen</Button>
                <Button size="sm" variant="outline" onClick={() => window.confirm("Neuen Link erstellen? Der bisherige Link funktioniert dann nicht mehr.") && save({ enabled: true, renew: true })} data-testid="gallery-share-renew"><RefreshCw size={14} className="mr-1.5" /> Neuer Link</Button>
              </div>
            </>}
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function OccupancyPlan() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [tier, setTier] = useState("");
  const [gType, setGType] = useState("");
  const [view, setView] = useState("liste");
  const [gfx, setGfx] = useState({});
  const [banners, setBanners] = useState([]);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  useEffect(() => {
    if (view === "galerie") {
      api.get("/partner-graphics/overview/list").then((r) => setGfx(Object.fromEntries(r.data.map((g) => [`${g.customer_id}|${g.space_id}`, g])))).catch(() => {});
      api.get("/banner/gallery/all").then((r) => setBanners(r.data)).catch(() => {});
    }
  }, [view]);
  useEffect(() => { api.get("/leistungen/plan").then((r) => setData(r.data)).catch(() => setData({ types: [], today: "" })); }, []);
  const counts = useMemo(() => {
    const all = (data?.types || []).flatMap((t) => t.spaces);
    return { alle: all.length, ...Object.fromEntries(["frei", "belegt", "bald"].map((k) => [k, all.filter((s) => status(s, data.today) === k).length])) };
  }, [data]);
  if (!data) return null;
  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="occupancy-plan">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-3"><CalendarRange size={26} /> Belegungsplan</h1>
          <p className="text-slate-500 mt-1">Alle Werbeflächen mit Partner und Laufzeit-Ende. Gelb: endet in den nächsten {SOON_DAYS} Tagen · Grün: frei.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        {isAdmin && <GalleryShare types={data.types} />}
        <div className="flex items-center rounded-md border border-slate-200 bg-white">
          <select value={gType} onChange={(e) => setGType(e.target.value)} className="h-9 pl-2 pr-1 text-sm bg-transparent border-r border-slate-200" data-testid="plan-graphics-type">
            <option value="">Alle Leistungsarten</option>
            {data.types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button type="button" onClick={() => downloadBlob("/partner-graphics/overview/pdf", { ...(gType ? { type_id: gType } : {}), ...(tier ? { tier } : {}) }, `Grafik-Uebersicht ${data.today}.pdf`)}
            className="h-9 px-3 text-sm font-medium inline-flex items-center gap-1.5 hover:bg-slate-50" data-testid="plan-graphics-pdf-button"><Images size={15} /> Grafiken als PDF</button>
        </div>
        <Button variant="outline" onClick={() => downloadBlob("/leistungen/plan.pdf", tier ? { tier } : {}, `Belegungsplan ${data.today}${tier ? ` ${TIER_META[tier][0]}` : ""}.pdf`)} data-testid="plan-pdf-button"><FileDown size={15} className="mr-1.5" /> Als PDF</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {[["alle", "Alle Flächen"], ["frei", "Frei"], ["bald", "Läuft bald aus"], ["belegt", "Belegt"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`plan-filter-${k}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{l} ({counts[k]})</button>
        ))}
        <span className="w-px h-6 bg-slate-200 mx-1 self-center" />
        {[["gold", "Gold"], ["silber", "Silber"], ["bronze", "Bronze"]].map(([k, l]) => (
          <button key={k} onClick={() => setTier((v) => (v === k ? "" : k))} data-testid={`plan-filter-tier-${k}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${tier === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{l}</button>
        ))}
        <span className="w-px h-6 bg-slate-200 mx-1 self-center" />
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 ml-auto">
          {[["liste", "Liste", List], ["galerie", "Galerie", LayoutGrid]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`plan-view-${k}`}
              className={`px-3 py-1 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${view === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}><Icon size={14} /> {l}</button>
          ))}
        </div>
      </div>
      {data.types.filter((t) => !gType || t.id === gType).map((t) => (view === "galerie"
        ? <TypeGallery key={t.id} type={t} today={data.today} filter={filter} tier={tier} gfx={gfx} />
        : <TypeTable key={t.id} type={t} today={data.today} filter={filter} tier={tier} />))}
      {view === "galerie" && <BannerGallery banners={banners} today={data.today} typeId={gType} />}
      {tier && counts.alle > 0 && !data.types.some((t) => t.spaces.some((s) => s.bookings.some((b) => b.tier === tier))) && <p className="text-slate-400" data-testid="plan-tier-empty">Keine Werbeflächen mit Kategorie {TIER_META[tier][0]} belegt.</p>}
      {counts.alle === 0 && <p className="text-slate-400">Noch keine Flächen im <Link to="/partner/leistungskatalog" className="underline">Leistungskatalog</Link> angelegt.</p>}
    </div>
  );
}
